import { useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Stepper } from "@/components/ui/stepper";
import { ChevronLeft, ChevronRight, Copy, CheckCircle2, Loader2 } from "lucide-react";
import { useI18n } from '@/hooks/useI18n';
import { ShareLinkStep2Configure } from "./ShareLinkStep2Configure";
import { ShareLinkStep3Generate } from "./ShareLinkStep3Generate";
import { DashboardItinerary } from "@/lib/models/dashboard";
import { useCreateSharedLink } from "@/lib/shared-proposal/hooks/useCreateSharedLink";
import { toast } from "@/hooks/useToast";
import { PaymentOptionsForm } from "@/lib/payments/components/PaymentOptionsForm";
import { useProposalPaymentStep } from "@/lib/payments/hooks/useProposalPaymentStep";
import { paymentOptionsErrorKey } from "@/lib/payments/hooks/usePaymentOptions";

interface ShareLinkFlowProps {
  initialItinerary?: DashboardItinerary;
  onBack: () => void;
  onComplete: (data: { shareUrl: string; linkPassword?: string; itinerary: DashboardItinerary }) => void;
  footerContainer?: HTMLElement | null;
}

export const ShareLinkFlow = ({
  initialItinerary,
  onBack,
  onComplete,
  footerContainer,
}: ShareLinkFlowProps) => {
  const { t } = useI18n();
  const [currentStep, setCurrentStep] = useState(1);
  const [linkPassword, setLinkPassword] = useState('');
  const [linkExpiry, setLinkExpiry] = useState<string | null>("30");

  const { generateLink, isGenerating, sharedLink } = useCreateSharedLink();

  // Anche dal link rapido il cliente paga: i metodi si scelgono qui come nell'invio in piattaforma.
  const payment = useProposalPaymentStep(initialItinerary?.id ?? '');

  const STEP_CONFIGURE = 1;
  const STEP_PAYMENT = 2;
  const STEP_GENERATE = 3;
  const steps = [
    { label: t('itinerary.configureLink') },
    { label: t('paymentOptions.review.title') },
    { label: t('itinerary.generateLink') },
  ];

  const handleNext = () => {
    if (currentStep === STEP_PAYMENT && !payment.validate()) return;
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      onBack();
    }
  };

  const handleGenerateLink = async () => {
    if (!initialItinerary) return;

    try {
      await payment.save();
    } catch (error) {
      toast({ title: t(paymentOptionsErrorKey(error)), variant: "destructive" });
      return;
    }

    try {
      // Convert days to ISO date
      let expiresAt: string | undefined = undefined;
      if (linkExpiry) {
        const days = parseInt(linkExpiry, 10);
        const date = new Date();
        date.setDate(date.getDate() + days);
        expiresAt = date.toISOString();
      }

      const result = await generateLink(
        initialItinerary.id,
        linkPassword,
        expiresAt
      );

      if (result) {
        toast({
          variant: "success",
          title: t("itinerary.linkGenerated"),
          description: t("itinerary.linkGeneratedDescription"),
        });
        onComplete({
          shareUrl: result.shareUrl,
          linkPassword: linkPassword || undefined,
          itinerary: initialItinerary,
        });
      } else {
        toast({
          title: t("common.error"),
          description: t("itinerary.linkGenerationError"),
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error generating link:', error);
      toast({
        title: t("common.error"),
        description: t("itinerary.linkGenerationError"),
        variant: "destructive",
      });
    }
  };

  const canProceedFromStep1 = linkPassword.trim().length > 0; // Password is required

  return (
    <>
      <div className="space-y-6 px-6 py-6">
        <Stepper steps={steps} currentStep={currentStep} />

        <div className="min-h-[400px]">
        {currentStep === STEP_CONFIGURE && (
          <ShareLinkStep2Configure
            linkPassword={linkPassword}
            setLinkPassword={setLinkPassword}
            linkExpiry={linkExpiry}
            setLinkExpiry={setLinkExpiry}
          />
        )}

        {currentStep === STEP_PAYMENT && (
          payment.form ? (
            <PaymentOptionsForm
              value={payment.form}
              onChange={payment.onChange}
              errors={payment.errors}
              stripeActive={payment.stripeActive}
              defaults={payment.defaults}
            />
          ) : (
            <div className="flex justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          )
        )}

        {currentStep === STEP_GENERATE && initialItinerary && (
          <ShareLinkStep3Generate
            itinerary={initialItinerary}
            linkPassword={linkPassword}
            linkExpiry={linkExpiry}
            onGenerate={handleGenerateLink}
            isGenerating={isGenerating || payment.isSaving}
            sharedLink={sharedLink}
          />
        )}
        </div>
      </div>

      {/* Navigation buttons — portaled into the Modal's fixed footer (outside the scroll area) */}
      {footerContainer && createPortal(
        <>
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={isGenerating}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            {currentStep === 1 ? t('common.back') : t('common.previous')}
          </Button>

          {currentStep < STEP_GENERATE && (
            <Button
              onClick={handleNext}
              disabled={
                (currentStep === STEP_CONFIGURE && !canProceedFromStep1) ||
                (currentStep === STEP_PAYMENT && payment.isLoading)
              }
            >
              {t('common.next')}
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </>,
        footerContainer
      )}
    </>
  );
};
